import { Link } from "react-router";
import { Aviso, Botao, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DA_ORIGEM,
  ROTULO_DA_URGENCIA_SUGERIDA,
  ROTULO_DO_SENTIMENTO,
  useAnalisarMensagem,
  useEstadoDaAssistencia,
  useResumirConversa,
  useSugerirResposta,
} from "../../nucleo/assistenciaIa";
import type { AnaliseDaMensagem } from "../../nucleo/assistenciaIa";
import { ROTULO_DA_URGENCIA } from "../../nucleo/tickets";
import type { UrgenciaDoTicket } from "../../nucleo/tickets";

interface Props {
  ticketId: string;
  /** Recebe o rascunho para pôr no campo de resposta. O envio é sempre da pessoa. */
  aoSugerirResposta: (texto: string) => void;
  /** Sem campo de resposta na tela (chamado mesclado), não há para onde levar o rascunho. */
  podeSugerir: boolean;
}

const TOM_DO_SENTIMENTO = { positivo: "ok", neutro: "neutro", negativo: "atencao" } as const;

/**
 * A assistência por IA do chamado — só para a equipe, só no console.
 *
 * Três ajudas: resumir a conversa, sugerir uma resposta e ler o sentimento e a urgência da última
 * mensagem do cliente. Nenhuma altera o chamado: o resumo é para ler, o rascunho vai para o campo
 * de resposta para a pessoa editar, e a classificação é uma sugestão. Com o assistente desligado
 * (o estado da produção hoje) a tela diz isso e os botões ficam indisponíveis — sem erro.
 */
export function AssistenciaDeIa({ ticketId, aoSugerirResposta, podeSugerir }: Props) {
  const estado = useEstadoDaAssistencia();
  const resumir = useResumirConversa(ticketId);
  const sugerir = useSugerirResposta(ticketId);
  const analisar = useAnalisarMensagem(ticketId);

  if (estado.isPending) return <p role="status">Verificando a assistência por IA…</p>;
  // Sem saber o estado, o certo é não oferecer: o servidor decide e a tela não adivinha.
  if (estado.isError) return null;

  const disponivel = estado.data.disponivel;
  const ocupado = resumir.isPending || sugerir.isPending || analisar.isPending;

  return (
    <Cartao
      titulo="Assistência por IA"
      acessorio={<Chip estado="neutro">Só equipe</Chip>}
      className="mb-4"
      data-testid="assistencia-de-ia"
    >
      {disponivel ? (
        <p className="cartao__texto mb-3">
          A IA sugere; quem decide é você. Nada daqui muda o chamado nem é enviado ao cliente.
        </p>
      ) : (
        <Aviso tom="atencao" className="mb-3" data-testid="assistencia-desligada">
          A assistência por IA está desligada neste ambiente, então os botões abaixo ficam
          indisponíveis. O atendimento segue normalmente.
          {estado.data.motivo ? <span className="block nota-rodape mt-1">{estado.data.motivo}</span> : null}
        </Aviso>
      )}

      <div className="flex flex-wrap gap-2">
        <Botao
          tamanho="pequeno"
          variante="fantasma"
          disabled={!disponivel || ocupado}
          onClick={() => resumir.mutate()}
        >
          {resumir.isPending ? "Resumindo…" : "Resumir conversa"}
        </Botao>
        <Botao
          tamanho="pequeno"
          variante="fantasma"
          disabled={!disponivel || ocupado || !podeSugerir}
          onClick={() => sugerir.mutate(undefined, { onSuccess: (r) => aoSugerirResposta(r.texto) })}
        >
          {sugerir.isPending ? "Redigindo…" : "Sugerir resposta"}
        </Botao>
        <Botao
          tamanho="pequeno"
          variante="fantasma"
          disabled={!disponivel || ocupado}
          onClick={() => analisar.mutate()}
        >
          {analisar.isPending ? "Analisando…" : "Analisar última mensagem"}
        </Botao>
      </div>

      {resumir.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(resumir.error)}</Aviso> : null}
      {sugerir.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(sugerir.error)}</Aviso> : null}
      {analisar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(analisar.error)}</Aviso> : null}

      {resumir.data ? (
        <div className="mt-3" data-testid="resumo-da-conversa">
          <p className="nota-rodape">Resumo da conversa (inclui as notas internas — não compartilhe com o cliente)</p>
          <p className="texto-preservado">{resumir.data.texto}</p>
        </div>
      ) : null}

      {sugerir.data ? (
        <div className="mt-3" data-testid="rascunho-sugerido">
          <Aviso tom="sucesso">
            Rascunho colocado no campo de resposta, depois do que já estava lá. Leia, edite e envie
            só se estiver certo.
          </Aviso>
          {sugerir.data.artigos.length > 0 ? (
            <p className="nota-rodape mt-2">
              Artigos citados:{" "}
              {sugerir.data.artigos.map((artigo, indice) => (
                <span key={artigo.id}>
                  {indice > 0 ? ", " : ""}
                  <Link to={`/admin/conhecimento/${artigo.id}`}>{artigo.titulo}</Link>
                </span>
              ))}
            </p>
          ) : (
            <p className="nota-rodape mt-2">Nenhum artigo publicado combinou com este chamado.</p>
          )}
        </div>
      ) : null}

      {analisar.data ? <ResultadoDaAnalise analise={analisar.data} /> : null}
    </Cartao>
  );
}

function ResultadoDaAnalise({ analise }: { analise: AnaliseDaMensagem }) {
  const sugerida = ROTULO_DA_URGENCIA_SUGERIDA[analise.urgencia_sugerida];
  const atual = ROTULO_DA_URGENCIA[analise.urgencia_atual as UrgenciaDoTicket] ?? analise.urgencia_atual;
  return (
    <div className="mt-3" data-testid="analise-da-mensagem">
      <div className="flex flex-wrap items-center gap-2">
        <Chip estado={TOM_DO_SENTIMENTO[analise.sentimento]}>
          Sentimento: {ROTULO_DO_SENTIMENTO[analise.sentimento]}
        </Chip>
        <Chip estado="neutro">Urgência sugerida: {sugerida}</Chip>
        <span className="nota-rodape">Urgência do chamado hoje: {atual}</span>
      </div>
      <p className="nota-rodape mt-2">
        Lida de {ROTULO_DA_ORIGEM[analise.origem] ?? analise.origem}. {analise.justificativa}
      </p>
      <p className="nota-rodape">É só uma sugestão: nada no chamado foi alterado.</p>
    </div>
  );
}
